import _ from 'lodash';

type WidgetContent = Record<string, any>;
type PageVersionLike = {
  id?: number;
  pageId?: number;
  content?: WidgetContent;
};
type PageContainerLike = {
  id?: number;
  current?: PageVersionLike;
};

export const findParentIdInContent = (
  content: WidgetContent,
  widgetId: string
): string | undefined => {
  const configuredParentId = content[widgetId]?.config?.parentId;
  if (configuredParentId) return configuredParentId;

  const candidateParentIds = Object.keys(content).filter(
    parentId =>
      Array.isArray(content[parentId]?.children) &&
      content[parentId].children.includes(widgetId)
  );

  return (
    candidateParentIds.find(parentId => parentId !== 'root') ??
    candidateParentIds[0]
  );
};

export const isDescendantOfSection = (
  content: WidgetContent,
  widgetId: string,
  sectionId: string
): boolean => {
  let currentId = widgetId;
  const visited = new Set<string>();

  while (currentId && currentId !== 'root') {
    if (visited.has(currentId)) return false; // cycle detected in widget tree
    visited.add(currentId);
    if (currentId === sectionId) return true;
    currentId = findParentIdInContent(content, currentId);
  }

  return false;
};

/**
 * Collects every widget that belongs to a section subtree.
 *
 * @param content page widget content keyed by widget id
 * @param sectionId section root id to inspect
 * @returns descendant widget ids, excluding the section itself
 */
export const collectSectionDescendantIds = (
  content: WidgetContent,
  sectionId: string
): string[] =>
  Object.keys(content).filter(widgetId => {
    if (widgetId === 'root' || widgetId === sectionId) return false;
    return isDescendantOfSection(content, widgetId, sectionId);
  });

/**
 * Measures how far a section's descendants are offset from the section root.
 * Lower scores indicate a footer whose children are visually attached.
 *
 * @param content page widget content keyed by widget id
 * @param sectionId section root id to inspect
 * @returns max descendant offset from the section root, or Infinity when unavailable
 */
export const getSectionAlignmentScore = (
  content: WidgetContent,
  sectionId: string
): number => {
  const sectionY = content[sectionId]?.config?.yLocation;
  if (typeof sectionY !== 'number') return Number.POSITIVE_INFINITY;

  const deltas = collectSectionDescendantIds(content, sectionId)
    .map(widgetId => content[widgetId]?.config?.yLocation)
    .filter((yLocation): yLocation is number => typeof yLocation === 'number')
    .map(yLocation => Math.abs(yLocation - sectionY));

  if (!deltas.length) return Number.POSITIVE_INFINITY;

  return Math.max(...deltas);
};

/**
 * Recomputes descendant y positions from a canonical footer reference while
 * preserving the current page's own footer container position.
 *
 * @param content current page widget content
 * @param referenceContent canonical footer content used for offsets
 * @param sectionId footer section id
 * @returns normalized content with repaired descendant y locations
 */
export const normalizeSectionDescendantYLocations = (
  content: WidgetContent,
  referenceContent: WidgetContent,
  sectionId: string
): WidgetContent => {
  const sectionY = content[sectionId]?.config?.yLocation;
  const referenceSectionY = referenceContent[sectionId]?.config?.yLocation;

  if (typeof sectionY !== 'number' || typeof referenceSectionY !== 'number') {
    return content;
  }

  const normalizedContent = { ...content };

  collectSectionDescendantIds(referenceContent, sectionId).forEach(widgetId => {
    const widget = normalizedContent[widgetId];
    const referenceWidget = referenceContent[widgetId];
    const referenceY = referenceWidget?.config?.yLocation;

    if (!widget || typeof referenceY !== 'number') return;
    if (!isDescendantOfSection(normalizedContent, widgetId, sectionId)) return;

    normalizedContent[widgetId] = {
      ...widget,
      config: {
        ...(widget.config || {}),
        yLocation: sectionY + (referenceY - referenceSectionY),
      },
    };
  });

  return normalizedContent;
};

/**
 * Coerces a page container or raw page version into a page-version-like shape.
 *
 * @param page page container or raw page version
 * @returns the page version payload when available
 */
export const asPageVersionLike = (
  page?: PageVersionLike | PageContainerLike
): PageVersionLike | undefined => {
  if (!page) return undefined;
  if ('current' in page) {
    return page.current;
  }
  return page;
};

/**
 * Chooses the best aligned footer as a reference for repairing other pages.
 * Candidates are ranked by how tightly their descendants sit under the section
 * root; the tightest is returned. No hard alignment threshold is applied, so
 * legitimately tall or grouped footers still qualify as long as at least one
 * measurable descendant exists.
 *
 * @param pages pages or page containers to inspect
 * @param sectionId footer section id
 * @param excludePageId optional page id to skip
 * @returns reference footer content when a suitable page exists
 */
export const getBestSectionReferenceContent = (
  pages: Array<PageVersionLike | PageContainerLike | undefined>,
  sectionId: string,
  excludePageId?: number
): WidgetContent | undefined => {
  const candidates = pages
    .map(page => {
      const currentPage = asPageVersionLike(page);
      if (!currentPage?.content) {
        return null;
      }

      const candidatePageId = currentPage.pageId ?? currentPage.id;
      if (excludePageId && candidatePageId === excludePageId) {
        return null;
      }

      const score = getSectionAlignmentScore(currentPage.content, sectionId);

      // Infinity score = no measurable descendants; cannot serve as reference.
      if (!Number.isFinite(score)) {
        return null;
      }

      return {
        content: currentPage.content,
        score,
      };
    })
    .filter(Boolean) as Array<{ content: WidgetContent; score: number }>;

  return candidates.sort((a, b) => a.score - b.score)[0]?.content;
};

/**
 * Repairs a page footer against a canonical footer reference while preserving
 * the page's own footer container position.
 *
 * @param pageVersion page version content to normalize
 * @param referenceContent canonical footer content used for offsets
 * @param sectionId footer section id
 * @param normalizeSectionContent callback used to canonicalize section structure
 * @returns page copy with repaired footer descendants
 */
export const normalizePageSectionContent = <T extends PageVersionLike>(
  pageVersion: T,
  referenceContent: WidgetContent | undefined,
  sectionId: string,
  normalizeSectionContent?: (content: WidgetContent, sectionId: string) => void
): T => {
  if (!pageVersion?.content || !referenceContent) {
    return pageVersion;
  }

  const normalizedContent = _.cloneDeep(pageVersion.content) as WidgetContent;

  normalizeSectionContent?.(normalizedContent, sectionId);

  const repairedContent = normalizeSectionDescendantYLocations(
    normalizedContent,
    referenceContent,
    sectionId
  );

  return {
    ...pageVersion,
    content: repairedContent,
  };
};
